// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * An RFC 4180 CSV writer with three deliberate deviations, each earning its keep:
 *
 *  1. Control characters — including the newlines inside a schema description — become VISIBLE
 *     escapes, so one record is always one physical line. A CSV whose row count differs from its
 *     line count cannot be `wc -l`'d or `grep`'d, and this file exists to be grepped.
 *  2. `;` forces quoting, because German, French and Spanish Excel locales split on it.
 *  3. A leading `=`, `+`, `-` or `@` gets a prefix — a tool description beginning `=IMPORTED` is a
 *     formula the moment someone opens the file in a spreadsheet.
 *
 * UTF-8 BOM and CRLF for the same reason: Excel is the second-most-likely consumer after grep.
 */

function escapeCell(value: unknown): string {
  let s = value === null || value === undefined ? '' : String(value);

  s = s.replace(/[\u0000-\u001f]/g, (c) => {
    if (c === '\n') return '\\n';
    if (c === '\r') return '\\r';
    if (c === '\t') return '\\t';
    return '\\x' + c.charCodeAt(0).toString(16).padStart(2, '0');
  });

  if (/^[=+\-@]/.test(s)) s = "'" + s;

  if (/["',;]/.test(s) || /^\s|\s$/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
}

export function toCsv(headers: readonly string[], rows: readonly (readonly unknown[])[]): string {
  const lines = [headers.map(escapeCell).join(',')];
  for (const row of rows) lines.push(row.map(escapeCell).join(','));
  return '\uFEFF' + lines.join('\r\n') + '\r\n';
}
